import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { postDemoBooking } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar, CheckCircle2, Loader2 } from "lucide-react";

const DAY_BY_SOLUTION = {
  lob_model: "Mardi / Tuesday",
  lob_automation: "Jeudi / Thursday",
  lob_rh: "Vendredi / Friday",
  lob_decision: "Lundi / Monday",
  lob_leadcard: "Mercredi / Wednesday",
  lob_speaker: "Samedi / Saturday",
};

export const DemoBooking = forwardRef((props, ref) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    phone: "",
    lob_solution: "",
    preferred_date: "",
    revenue_range: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useImperativeHandle(ref, () => ({
    selectSolution: (key) => {
      setForm((f) => ({ ...f, lob_solution: key }));
      const el = document.getElementById("demo");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    },
  }));

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.lob_solution) {
      toast.error("Merci de choisir une solution BICENTER AI");
      return;
    }
    if (!form.revenue_range) {
      toast.error("Merci d'indiquer votre palier de chiffre d'affaires");
      return;
    }
    setLoading(true);
    try {
      await postDemoBooking(form);
      setDone(true);
      toast.success(t.demo.form.success);
      setForm({
        name: "", email: "", company: "", role: "", phone: "",
        lob_solution: "", preferred_date: "", revenue_range: "", message: "",
      });
    } catch (err) {
      toast.error(t.demo.form.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="demo" className="relative py-28 md:py-36" data-testid="demo-section">
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/3 w-[420px] h-[420px] rounded-full bg-bicenter-violet/15 blur-[160px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left side */}
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="text-xs uppercase tracking-[0.4em] text-bicenter-cyan font-mono">{t.demo.kicker}</p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-light tracking-tight text-white leading-[1.05]">
              {t.demo.title}
            </h2>
            <p className="mt-6 text-zinc-400 font-body leading-relaxed">{t.demo.subtitle}</p>

            <div className="mt-10 border border-white/10 rounded-2xl p-6 bg-[#0c0c0e]">
              <div className="flex items-center gap-2 mb-5">
                <Calendar size={16} className="text-bicenter-cyan" />
                <p className="text-sm uppercase tracking-[0.28em] text-zinc-400">{t.demo.schedule_title}</p>
              </div>
              <ul className="space-y-3">
                {t.lob.items.map((s) => (
                  <li
                    key={s.key}
                    className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                  >
                    <span className="text-sm text-zinc-200 font-display tracking-wide">{s.name}</span>
                    <span className="text-xs text-gradient-mix font-medium">{s.day}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right side - Form */}
          <div className="lg:col-span-7">
            <div className="border-gradient-primary p-[1px] rounded-3xl">
              <div className="bg-[#0c0c0e] rounded-3xl p-8 md:p-10">
                {done ? (
                  <div className="flex flex-col items-center text-center py-12" data-testid="demo-success">
                    <div className="w-16 h-16 rounded-full bg-bicenter-cyan/10 flex items-center justify-center mb-6 border border-bicenter-cyan/30">
                      <CheckCircle2 size={28} className="text-bicenter-cyan" />
                    </div>
                    <h3 className="font-display text-2xl text-white mb-3">Confirmation BICENTER AI</h3>
                    <p className="text-zinc-300 max-w-md leading-relaxed">{t.demo.form.success}</p>
                    <div className="mt-6 px-5 py-3 rounded-full bg-gradient-to-r from-bicenter-cyan/15 to-bicenter-violet/15 border border-bicenter-cyan/30 text-sm text-white">
                      🎁 Diagnostic 15 min offert · Confirmation par email
                    </div>
                    <button
                      onClick={() => setDone(false)}
                      className="mt-6 text-xs text-zinc-500 hover:text-white underline"
                    >
                      Faire une nouvelle demande
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-5" data-testid="demo-form">
                    <div>
                      <h3 className="font-display text-2xl text-white font-medium">{t.demo.form.title}</h3>
                      <p className="text-sm text-zinc-500 mt-1">{t.demo.form.sub}</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <Field label={t.demo.form.name} required>
                        <Input
                          required
                          value={form.name}
                          onChange={(e) => update("name", e.target.value)}
                          data-testid="demo-input-name"
                          className="bg-white/[0.03] border-white/10 text-white h-12"
                        />
                      </Field>
                      <Field label={t.demo.form.email} required>
                        <Input
                          required
                          type="email"
                          value={form.email}
                          onChange={(e) => update("email", e.target.value)}
                          data-testid="demo-input-email"
                          className="bg-white/[0.03] border-white/10 text-white h-12"
                        />
                      </Field>
                      <Field label={t.demo.form.company} required>
                        <Input
                          required
                          value={form.company}
                          onChange={(e) => update("company", e.target.value)}
                          data-testid="demo-input-company"
                          className="bg-white/[0.03] border-white/10 text-white h-12"
                        />
                      </Field>
                      <Field label={t.demo.form.role}>
                        <Input
                          value={form.role}
                          onChange={(e) => update("role", e.target.value)}
                          data-testid="demo-input-role"
                          className="bg-white/[0.03] border-white/10 text-white h-12"
                        />
                      </Field>
                      <Field label={t.demo.form.phone}>
                        <Input
                          value={form.phone}
                          onChange={(e) => update("phone", e.target.value)}
                          data-testid="demo-input-phone"
                          className="bg-white/[0.03] border-white/10 text-white h-12"
                        />
                      </Field>
                      <Field label={t.demo.form.date}>
                        <Input
                          type="date"
                          value={form.preferred_date}
                          onChange={(e) => update("preferred_date", e.target.value)}
                          data-testid="demo-input-date"
                          className="bg-white/[0.03] border-white/10 text-white h-12"
                        />
                      </Field>
                    </div>

                    <Field label={t.demo.form.solution} required>
                      <Select value={form.lob_solution} onValueChange={(v) => update("lob_solution", v)}>
                        <SelectTrigger
                          data-testid="demo-select-solution"
                          className="bg-white/[0.03] border-white/10 text-white h-12"
                        >
                          <SelectValue placeholder={t.demo.form.select_solution} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121214] border-white/10 text-white">
                          {t.lob.items.map((s) => (
                            <SelectItem key={s.key} value={s.key} className="focus:bg-white/10">
                              <div className="flex items-center justify-between gap-4 w-full">
                                <span>{s.name}</span>
                                <span className="text-xs text-zinc-400">· {s.day}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {form.lob_solution && (
                        <p className="text-xs text-bicenter-cyan mt-2">
                          ✓ Créneau dédié : {DAY_BY_SOLUTION[form.lob_solution]}
                        </p>
                      )}
                    </Field>

                    <Field label={t.demo.form.revenue} required>
                      <Select value={form.revenue_range} onValueChange={(v) => update("revenue_range", v)}>
                        <SelectTrigger
                          data-testid="demo-select-revenue"
                          className="bg-white/[0.03] border-white/10 text-white h-12"
                        >
                          <SelectValue placeholder={t.demo.form.revenue_select} />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121214] border-white/10 text-white">
                          {["0-30k", "30k-80k", "80k-150k", "150k+"].map((k) => (
                            <SelectItem key={k} value={k} className="focus:bg-white/10">
                              {t.demo.form.revenue_ranges[k]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <Field label={t.demo.form.message}>
                      <Textarea
                        rows={4}
                        value={form.message}
                        onChange={(e) => update("message", e.target.value)}
                        data-testid="demo-input-message"
                        className="bg-white/[0.03] border-white/10 text-white"
                      />
                    </Field>

                    <Button
                      type="submit"
                      disabled={loading}
                      data-testid="demo-submit-button"
                      className="w-full h-14 bg-gradient-to-r from-bicenter-cyan to-bicenter-blue text-white text-base font-medium hover:shadow-[0_0_30px_rgba(0,169,255,0.45)] transition-all"
                    >
                      {loading ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 size={16} className="animate-spin" /> {t.common.loading}
                        </span>
                      ) : (
                        t.demo.form.submit
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

const Field = ({ label, required, children }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-400">
      {label} {required && <span className="text-bicenter-magenta">*</span>}
    </Label>
    {children}
  </div>
);
